import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-datepicker/dist/react-datepicker.css";

import Home from "./pages/Home";
import Jobs from "./pages/Jobs";
import JobsDetail from "./pages/JobsDetail";
import NotFound from "./pages/NotFound";
import News from "./pages/News";
import Events from "./pages/Events";
import NewsDetail from "./pages/NewsDetail";
import EventsDetail from "./pages/EventsDetail";

import ScrollToTop from "./components/atoms/ScrollToTop";
import PageLoader from "./components/atoms/PageLoader";

import AdminLogin from "./pages/Auth/AdminLogin";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import Register from "./pages/Auth/Register";
import OtpVerificationUser from "./pages/Auth/OtpVerificationUser";
import PersonalInformationUser from "./pages/Auth/PersonalInformationUser";
import OtpVerificationAdmin from "./pages/Auth/OtpVerificationAdmin";
import PersonalInformationAdmin from "./pages/Auth/PersonalInformationAdmin";
import CompanyInformationAdmin from "./pages/Auth/CompanyInformationAdmin";

import AdminRoute from "./components/auth/AdminRoute";
import UserRoute from "./components/auth/UserRoute";
import AuthStepRoute from "./components/auth/AuthStepRoute";

import AdminJobPost from "./pages/admin/AdminJobPost";
import AdminCompanyProfile from "./pages/admin/AdminCompanyProfile";
import AdminUserProfile from "./pages/admin/AdminUserProfile";
import AdminJobPostCreate from "./pages/admin/AdminJobPostCreate";
import AdminManageCandidatesPage from "./pages/admin/AdminManageCandidatesPage";
import AdminJobPostEdit from "./pages/admin/AdminJobPostEdit";

import UserProfile from "./pages/user/UserProfile";
import UserDashboard from "./pages/user/UserDashboard";

import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";

import OurService from "./pages/OurService";
import Package from "./pages/Package";
import FloatingWhatsApp from "./components/molecules/FloatingWhatsApp";

function AppContent() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      {loading && <PageLoader />}
      <ScrollToTop />
      <FloatingWhatsApp />

      <Routes>

        {/* Redirect default ke /en */}
        <Route path="/" element={<Navigate to="/en" replace />} />

        {/* ===================== 🌐 ENGLISH ===================== */}
        <Route path="/en">

          {/* PUBLIC */}
          <Route path="homepage" element={<Home />} />
          <Route path="blog/news" element={<News />} />
          <Route path="blog/events" element={<Events />} />
          <Route path="blog/news/:slug" element={<NewsDetail />} />
          <Route path="blog/events/:slug" element={<EventsDetail />} />
          <Route index element={<Navigate to="jobs" replace />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-condition" element={<Terms />} />
          <Route path="our-service" element={<OurService />} />
          <Route path="package" element={<Package />} />
          <Route path="jobs/:slug" element={<JobsDetail />} />

          {/* AUTH PAGES */}
          <Route path="login" element={<Login />} />
          <Route path="login/admin" element={<AdminLogin />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />

          <Route path="login/user/otp-verification" element={<OtpVerificationUser />} />
          <Route path="login/user/personal-information" element={<PersonalInformationUser />} />

          <Route path="login/admin/otp-verification" element={<OtpVerificationAdmin />} />
          <Route path="login/admin/personal-information" element={<PersonalInformationAdmin />} />
          <Route path="login/admin/company-information" element={<CompanyInformationAdmin />} />

          <Route element={<AuthStepRoute />}>
            <Route path="login/admin/otp-verification" element={<OtpVerificationAdmin />} />
            <Route path="login/admin/personal-information" element={<PersonalInformationAdmin />} />
            <Route path="login/admin/company-information" element={<CompanyInformationAdmin />} />
          </Route>

          {/* USER PROTECTED */}
          <Route element={<UserRoute />}>
            <Route path="user/user-profile" element={<UserProfile />} />
            <Route path="user/user-dashboard" element={<UserDashboard />} />
          </Route>

          {/* ADMIN PROTECTED */}
          <Route element={<AdminRoute />}>
            <Route path="admin/job-post" element={<AdminJobPost />} />
            <Route path="admin/company-profile" element={<AdminCompanyProfile />} />
            <Route path="admin/user-profile" element={<AdminUserProfile />} />
            <Route path="admin/job-post/create" element={<AdminJobPostCreate />} />
            <Route path="admin/job-post/candidate/:id" element={<AdminManageCandidatesPage />} />
            <Route path="admin/job-post/edit/:id" element={<AdminJobPostEdit />} />
          </Route>

        </Route>

        {/* ===================== 🇮🇩 INDONESIAN ===================== */}
        <Route path="/id">

          {/* PUBLIC */}
          <Route path="homepage" element={<Home />} />
          <Route path="blog/news" element={<News />} />
          <Route path="blog/events" element={<Events />} />
          <Route path="blog/news/:slug" element={<NewsDetail />} />
          <Route path="blog/events/:slug" element={<EventsDetail />} />
          <Route index element={<Navigate to="jobs" replace />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-condition" element={<Terms />} />
          <Route path="our-service" element={<OurService />} />
          <Route path="package" element={<Package />} />
          <Route path="jobs/:slug" element={<JobsDetail />} />

          {/* AUTH */}
          <Route path="login" element={<Login />} />
          <Route path="login/admin" element={<AdminLogin />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route element={<AuthStepRoute />}>
            <Route path="login/user/otp-verification" element={<OtpVerificationUser />} />
            <Route path="login/user/personal-information" element={<PersonalInformationUser />} />

            <Route path="login/admin/otp-verification" element={<OtpVerificationAdmin />} />
            <Route path="login/admin/personal-information" element={<PersonalInformationAdmin />} />
            <Route path="login/admin/company-information" element={<CompanyInformationAdmin />} />
          </Route>

          <Route element={<AuthStepRoute />}>
            <Route path="login/admin/otp-verification" element={<OtpVerificationAdmin />} />
          </Route>

          {/* USER PROTECTED */}
          <Route element={<UserRoute />}>
            <Route path="user/user-profile" element={<UserProfile />} />
            <Route path="user/user-dashboard" element={<UserDashboard />} />
          </Route>
          {/* <Route path="user/user-profile" element={<UserProfile />} />
          <Route path="user/user-dashboard" element={<UserDashboard />} /> */}

          {/* ADMIN PROTECTED */}
          <Route element={<AdminRoute />}>
            <Route path="admin/job-post" element={<AdminJobPost />} />
            <Route path="admin/company-profile" element={<AdminCompanyProfile />} />
            <Route path="admin/user-profile" element={<AdminUserProfile />} />
            <Route path="admin/job-post/create" element={<AdminJobPostCreate />} />
            <Route path="admin/job-post/candidate/:id" element={<AdminManageCandidatesPage />} />
            <Route path="admin/job-post/edit/:id" element={<AdminJobPostEdit />} />
          </Route>

        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />

      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
